import React, { useEffect, useLayoutEffect, useRef, useState } from "react";
import { createPortal } from "react-dom";
import { toast } from "react-toastify";
import DatePicker from "react-datepicker";
import "react-datepicker/dist/react-datepicker.css";
import jsPDF from "jspdf";
import autoTable from "jspdf-autotable";
import onsiteproLogo from "../../assets/logo2 2.png";
import "./horizonTable.css";
import AdminTaylorImportModal, { adminIsPrimaryAdmin } from "./AdminTaylorImportModal";
import { ensureFormUuid } from "../../utils/formUuid";
import { buildSharePointFileName, toDdMmYyyy, addedByName } from "../../utils/formPdfHelpers";

import { canEditDeleteRecords } from "../../utils/rolePermissions";
const THEME = "#8b1634";

const SUITABLE_OPTIONS = [
  { value: "Y", label: "Y" },
  { value: "N", label: "N" },
  { value: "NA", label: "NA" }
];

const YES_NO_OPTIONS = [
  { value: "", label: "—" },
  { value: "Y", label: "Y" },
  { value: "N", label: "N" }
];

function isolationPossessionYesNoForSelect(value) {
  if (value === true) return "Y";
  if (value === false) return "N";
  const s = String(value ?? "").trim();
  if (!s) return "";
  if (s === "Y" || s === "N") return s;
  const upper = s.toUpperCase();
  if (upper === "Y" || upper === "YES") return "Y";
  if (upper === "N" || upper === "NO") return "N";
  return "";
}

const THEME_SELECT_ITEM_HEIGHT = 34;
const THEME_SELECT_MAX_VISIBLE = 6;

function ThemeSelect({ value, onChange, options, placeholder = "—", compact = false }) {
  const [open, setOpen] = useState(false);
  const [menuRect, setMenuRect] = useState(null);
  const rootRef = useRef(null);
  const triggerRef = useRef(null);
  const menuRef = useRef(null);

  const needsScroll = options.length > THEME_SELECT_MAX_VISIBLE;
  const menuMaxHeight = needsScroll ? THEME_SELECT_MAX_VISIBLE * THEME_SELECT_ITEM_HEIGHT : undefined;

  const updateMenuPosition = () => {
    if (!triggerRef.current) return;
    const rect = triggerRef.current.getBoundingClientRect();
    const menuHeight = needsScroll
      ? menuMaxHeight
      : options.length * THEME_SELECT_ITEM_HEIGHT;
    const spaceBelow = window.innerHeight - rect.bottom - 8;
    const spaceAbove = rect.top - 8;
    const openUp = spaceBelow < menuHeight + 8 && spaceAbove > spaceBelow;
    const top = openUp ? Math.max(8, rect.top - menuHeight - 4) : rect.bottom + 4;

    setMenuRect({
      top,
      left: rect.left,
      width: rect.width,
      openUp
    });
  };

  useLayoutEffect(() => {
    if (!open) {
      setMenuRect(null);
      return undefined;
    }
    updateMenuPosition();
    const onReposition = () => updateMenuPosition();
    window.addEventListener("resize", onReposition);
    window.addEventListener("scroll", onReposition, true);
    return () => {
      window.removeEventListener("resize", onReposition);
      window.removeEventListener("scroll", onReposition, true);
    };
  }, [open, options.length, needsScroll, menuMaxHeight]);

  useEffect(() => {
    const onDocClick = (e) => {
      if (rootRef.current?.contains(e.target)) return;
      if (menuRef.current?.contains(e.target)) return;
      setOpen(false);
    };
    document.addEventListener("mousedown", onDocClick);
    return () => document.removeEventListener("mousedown", onDocClick);
  }, []);

  const selected = options.find((o) => o.value === value);
  const display = selected ? selected.label : placeholder;

  const menu =
    open && menuRect
      ? createPortal(
          <div
            ref={menuRef}
            className={needsScroll ? "theme-select-menu-scroll" : ""}
            style={{
              position: "fixed",
              top: menuRect.top,
              left: menuRect.left,
              width: menuRect.width,
              zIndex: 2000,
              background: "#fff",
              border: `1px solid ${THEME}`,
              borderRadius: 8,
              boxShadow: "0 10px 28px rgba(139, 22, 52, 0.22)",
              overflowX: "hidden",
              overflowY: needsScroll ? "auto" : "hidden",
              maxHeight: needsScroll ? menuMaxHeight : undefined,
              overscrollBehavior: "contain",
              ...(needsScroll
                ? { scrollbarWidth: "thin", scrollbarColor: `${THEME} #f8fafc` }
                : {})
            }}
          >
            {options.map((opt, index) => {
              const active = opt.value === value;
              const isLast = index === options.length - 1;
              return (
                <button
                  key={opt.value === "" ? "__empty" : opt.value}
                  type="button"
                  onClick={() => {
                    onChange(opt.value);
                    setOpen(false);
                  }}
                  style={{
                    width: "100%",
                    minHeight: THEME_SELECT_ITEM_HEIGHT,
                    display: "flex",
                    alignItems: "center",
                    textAlign: "left",
                    padding: "0 12px",
                    fontSize: compact ? 11 : 12,
                    fontWeight: active ? 700 : 500,
                    color: active ? "#fff" : THEME,
                    background: active ? THEME : "#fff",
                    border: "none",
                    borderBottom: isLast ? "none" : "1px solid #f1f5f9",
                    cursor: "pointer",
                    transition: "background 0.15s ease, color 0.15s ease"
                  }}
                  onMouseEnter={(e) => {
                    if (!active) e.currentTarget.style.background = "rgba(139, 22, 52, 0.08)";
                  }}
                  onMouseLeave={(e) => {
                    if (!active) e.currentTarget.style.background = "#fff";
                  }}
                >
                  {opt.label}
                </button>
              );
            })}
          </div>,
          document.body
        )
      : null;

  return (
    <div ref={rootRef} style={{ position: "relative", width: "100%", minWidth: compact ? 56 : 72 }}>
      <button
        ref={triggerRef}
        type="button"
        onClick={() => setOpen((prev) => !prev)}
        style={{
          width: "100%",
          display: "flex",
          alignItems: "center",
          justifyContent: "space-between",
          gap: 6,
          padding: compact ? "4px 8px" : "6px 10px",
          fontSize: compact ? 11 : 12,
          fontWeight: 600,
          color: value ? THEME : "#64748b",
          background: "#fff",
          border: `1px solid ${open ? THEME : "#d1d5db"}`,
          borderRadius: 8,
          cursor: "pointer",
          boxShadow: open ? "0 0 0 2px rgba(139, 22, 52, 0.15)" : "none",
          transition: "border-color 0.15s ease, box-shadow 0.15s ease"
        }}
      >
        <span>{display}</span>
        <i className={`fa fa-chevron-${open ? "up" : "down"}`} style={{ fontSize: 9, color: THEME }} />
      </button>
      {menu}
    </div>
  );
}

const NARRATIVE_FIELDS = [
  { key: "dayDiary", title: "Day Diary" },
  { key: "vehicles", title: "Vehicles" },
  { key: "workingAtHeight", title: "Working at Height" },
  { key: "welfare", title: "Welfare" },
  { key: "waste", title: "Waste" },
  { key: "equipment", title: "Equipment" },
  { key: "ppe", title: "PPE" },
  { key: "toolboxTalksBriefed", title: "Toolbox Talks Briefed" }
];

function formatNarrativePdfValue(value) {
  if (value == null) return "—";
  const s = String(value).trim();
  return s || "—";
}

function pdfSafeText(value) {
  if (value == null) return "—";
  const s = String(value)
    .replace(/[^\x09\x0A\x0D\x20-\x7E\xA0-\xFF]/g, "")
    .replace(/\s+/g, " ")
    .trim();
  return s || "—";
}

function buildNarrativePdfSectionRows(record) {
  return [
    { num: "5", title: "Brief description of works inspected", value: record.briefDescription },
    ...NARRATIVE_FIELDS.map((field, index) => ({
      num: String(6 + index),
      title: field.title,
      value: record[field.key]
    }))
  ];
}

const DEFAULT_INSPECTION_ROWS = [
  { no: 1, section: "SAFETY", item: "First Aid" },
  { no: 2, section: "SAFETY", item: "Signs & Notices" },
  { no: 3, section: "SAFETY", item: "TBS/WPP" },
  { no: 4, section: "SAFETY", item: "PoWA" },
  { no: 5, section: "SAFETY", item: "Permit to Work (Breaking Ground/Hot Works/Working at Height etc.)" },
  { no: 6, section: "SAFETY", item: "IPAF/MEWP" },
  { no: 7, section: "SAFETY", item: "Ladders/Steps" },
  { no: 8, section: "SAFETY", item: "PASMA/Towers" },
  { no: 9, section: "SAFETY", item: "Manual Handling" },
  { no: 10, section: "SAFETY", item: "Pedestrian & Station Access" },
  { no: 11, section: "SAFETY", item: "COSHH" },
  { no: 12, section: "SAFETY", item: "Plant & Certification" },
  { no: 13, section: "SAFETY", item: "Housekeeping/Site Tidiness" },
  { no: 14, section: "SAFETY", item: "Tools" },
  { no: 15, section: "SAFETY", item: "Noise/Vibration/Dust (HAVS)" },
  { no: 16, section: "SAFETY", item: "Electrics/PAT" },
  { no: 17, section: "SAFETY", item: "PPE" },
  { no: 18, section: "SAFETY", item: "Fire Precautions/Provision" },
  { no: 19, section: "SAFETY", item: "Lighting" },
  { no: 20, section: "ENVIRONMENTAL", item: "Noise" },
  { no: 21, section: "ENVIRONMENTAL", item: "Waste Management" },
  { no: 22, section: "ENVIRONMENTAL", item: "Dewatering" },
  { no: 23, section: "ENVIRONMENTAL", item: "Spill Kit" },
  { no: 24, section: "ENVIRONMENTAL", item: "Fuel Storage/Prevention of Spillages" },
  { no: 25, section: "ENVIRONMENTAL", item: "Trees & Verges Protected" },
  { no: 26, section: "OTHER", item: "Rail Requirements" },
  { no: 27, section: "OTHER", item: "Colleague Wellbeing (Fatigue/Fitness to Work)" }
];

function getBaseUrl() {
  const apiUrl = process.env.REACT_APP_API_URL;
  return apiUrl?.endsWith("/") ? apiUrl.slice(0, -1) : apiUrl || "";
}

function parseStoredDate(value) {
  if (!value) return null;
  if (value instanceof Date) return isNaN(value.getTime()) ? null : value;
  const s = String(value).trim();
  const dmy = s.match(/^(\d{1,2})\/(\d{1,2})\/(\d{4})$/);
  if (dmy) {
    const day = parseInt(dmy[1], 10);
    const month = parseInt(dmy[2], 10) - 1;
    const year = parseInt(dmy[3], 10);
    const d = new Date(year, month, day);
    return isNaN(d.getTime()) ? null : d;
  }
  const ymd = s.match(/^(\d{4})-(\d{2})-(\d{2})/);
  if (ymd) {
    const year = parseInt(ymd[1], 10);
    const month = parseInt(ymd[2], 10) - 1;
    const day = parseInt(ymd[3], 10);
    const d = new Date(year, month, day);
    return isNaN(d.getTime()) ? null : d;
  }
  const t = new Date(s);
  return isNaN(t.getTime()) ? null : t;
}

function dateToYmd(d) {
  if (!d) return "";
  const y = d.getFullYear();
  const m = String(d.getMonth() + 1).padStart(2, "0");
  const day = String(d.getDate()).padStart(2, "0");
  return `${y}-${m}-${day}`;
}

function formatCellDate(value) {
  const d = parseStoredDate(value);
  if (!d) return value ? String(value) : "—";
  return d.toLocaleDateString("en-GB", { day: "2-digit", month: "short", year: "numeric" });
}

function parseInspectionItems(raw) {
  if (raw == null) return [];
  if (Array.isArray(raw)) return raw;
  if (typeof raw === "string") {
    try {
      const p = JSON.parse(raw);
      return Array.isArray(p) ? p : [];
    } catch {
      return [];
    }
  }
  return [];
}

function ensureInspectionItems(raw) {
  const list = parseInspectionItems(raw);
  return DEFAULT_INSPECTION_ROWS.map((def) => {
    const row = list.find((r) => Number(r.no) === def.no);
    const suitable = row?.suitable != null ? String(row.suitable) : "Y";
    return {
      no: def.no,
      section: def.section,
      item: def.item,
      suitable: ["Y", "N", "NA"].includes(suitable) ? suitable : "Y",
      // Carried through untouched: `comments` is the inspector's narrative for the row
      // and `actions` is the app's Yes/No flag. Both were being dropped here.
      actions: row?.actions != null ? String(row.actions) : "",
      comments: typeof row?.comments === "string" ? row.comments : ""
    };
  });
}

/** Rows that actually carry a note, for the Inspection Notes section. */
function getInspectionNotes(items) {
  return (items || []).filter((r) => typeof r?.comments === "string" && r.comments.trim() !== "");
}

function parseActionsTable(raw) {
  if (raw == null) return [];
  if (Array.isArray(raw)) return raw;
  if (typeof raw === "string") {
    try {
      const p = JSON.parse(raw);
      return Array.isArray(p) ? p : [];
    } catch {
      return [];
    }
  }
  return [];
}

function ensureActionsTable(raw) {
  const list = parseActionsTable(raw);
  if (!list.length) {
    return [{ ref: "", goodPracticeOrCorrectiveAction: "", responsibility: "", actionComplete: "" }];
  }
  return list.map((row) => ({
    ref: row.ref != null ? String(row.ref) : "",
    goodPracticeOrCorrectiveAction: row.goodPracticeOrCorrectiveAction != null ? String(row.goodPracticeOrCorrectiveAction) : "",
    responsibility: row.responsibility != null ? String(row.responsibility) : "",
    actionComplete: row.actionComplete != null ? String(row.actionComplete) : ""
  }));
}

function normalizeImageSource(value, fallbackMime = "image/jpeg") {
  if (!value) return "";

  let src = value;
  if (Array.isArray(src)) src = src[0];
  if (src == null) return "";

  if (typeof src !== "string") src = String(src);
  src = src.trim();
  if (!src) return "";

  if (src.startsWith("[")) {
    try {
      const parsed = JSON.parse(src);
      if (Array.isArray(parsed) && parsed.length) {
        src = String(parsed[0] || "").trim();
      }
    } catch {
      const firstQuoted = src.match(/^\[\s*["']([^"']+)["']/);
      if (firstQuoted?.[1]) {
        src = firstQuoted[1].trim();
      }
    }
  }

  if ((src.startsWith('"') && src.endsWith('"')) || (src.startsWith("'") && src.endsWith("'"))) {
    src = src.slice(1, -1).trim();
  }
  if (src.startsWith('["')) src = src.slice(2);
  if (src.startsWith("['")) src = src.slice(2);
  if (src.endsWith('"]')) src = src.slice(0, -2);
  if (src.endsWith("']")) src = src.slice(0, -2);
  if (src.startsWith("[") && src.endsWith("]")) {
    src = src.slice(1, -1).trim();
    if ((src.startsWith('"') && src.endsWith('"')) || (src.startsWith("'") && src.endsWith("'"))) {
      src = src.slice(1, -1).trim();
    }
  }

  if (!src) return "";
  if (src.startsWith("data:image")) return src;
  if (/^(https?:)?\/\//i.test(src)) return src;

  src = src.replace(/\\\//g, "/").replace(/\\n/g, "").replace(/\\r/g, "");
  const compact = src.replace(/\s+/g, "").replace(/,+$/, "");
  if (!compact) return "";
  let mime = fallbackMime;
  if (compact.startsWith("/9j")) mime = "image/jpeg";
  else if (compact.startsWith("iVBOR")) mime = "image/png";
  else if (compact.startsWith("R0lGOD")) mime = "image/gif";
  else if (compact.startsWith("UklGR")) mime = "image/webp";
  return `data:${mime};base64,${compact}`;
}

function normalizePhotos(value) {
  if (!value) return [];
  if (Array.isArray(value)) {
    return value.map((v) => normalizeImageSource(v)).filter(Boolean);
  }
  if (typeof value === "string") {
    const trimmed = value.trim();
    if (!trimmed) return [];
    if (trimmed.startsWith("[")) {
      try {
        const parsed = JSON.parse(trimmed);
        if (Array.isArray(parsed)) return normalizePhotos(parsed);
      } catch {}
    }
    const single = normalizeImageSource(trimmed);
    return single ? [single] : [];
  }
  return [];
}

function fileToDataUrl(file) {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = () => resolve(String(reader.result || ""));
    reader.onerror = () => reject(new Error("Failed to read image file"));
    reader.readAsDataURL(file);
  });
}

function getPdfImageType(imageSrc, fallbackType = "JPEG") {
  if (!imageSrc || typeof imageSrc !== "string") return fallbackType;
  let b64 = imageSrc;
  const idx = imageSrc.indexOf("base64,");
  if (idx >= 0) b64 = imageSrc.slice(idx + 7);
  b64 = b64.replace(/\s/g, "");
  if (b64.startsWith("/9j")) return "JPEG";
  if (b64.startsWith("iVBOR")) return "PNG";
  if (b64.startsWith("UklGR")) return "WEBP";
  const m = imageSrc.match(/^data:image\/([a-zA-Z0-9+.-]+);base64,/i);
  if (m?.[1]) {
    const subtype = m[1].toLowerCase();
    if (subtype === "png") return "PNG";
    if (subtype === "jpg" || subtype === "jpeg") return "JPEG";
    if (subtype === "webp") return "WEBP";
  }
  return fallbackType;
}

function RailHseqSiteInspectionPage() {
  const canEditDelete = canEditDeleteRecords();
  const [records, setRecords] = useState([]);
  const [loadingRecords, setLoadingRecords] = useState(true);
  const [appUsers, setAppUsers] = useState([]);
  const [search, setSearch] = useState("");
  const [page, setPage] = useState(1);
  const pageSize = 10;
  const [showEditModal, setShowEditModal] = useState(false);
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [showImportModal, setShowImportModal] = useState(false);
  const isAdminUser = adminIsPrimaryAdmin();
  const [editForm, setEditForm] = useState(null);
  const [deleteId, setDeleteId] = useState(null);
  const [uploadingKey, setUploadingKey] = useState(null);
  const [photoInputResetKey, setPhotoInputResetKey] = useState(0);

  useEffect(() => {
    fetchRecords();
    fetchAppUsers();
  }, []);

  async function fetchRecords() {
    setLoadingRecords(true);
    try {
      const token = localStorage.getItem("token");
      const res = await fetch(getBaseUrl() + "/api/rail-hseq-site-inspection", {
        headers: { Authorization: `Bearer ${token}` }
      });
      const data = await res.json();
      setRecords(res.ok && data.success && Array.isArray(data.data) ? data.data : []);
    } catch {
      setRecords([]);
    } finally {
      setLoadingRecords(false);
    }
  }

  async function fetchAppUsers() {
    try {
      const token = localStorage.getItem("token");
      const res = await fetch(getBaseUrl() + "/api/app-users", {
        headers: { Authorization: `Bearer ${token}` }
      });
      const data = await res.json();
      setAppUsers(res.ok && data.success && Array.isArray(data.data) ? data.data : []);
    } catch {
      setAppUsers([]);
    }
  }

  const filtered = records.filter((r) => {
    const fields = [
      r.projectLocation,
      r.date,
      r.weekEnding,
      r.completedBy,
      r.time,
      r.hseqAttendees,
      r.engineerAttendees,
      r.briefDescription
    ].map((v) => (v || "").toLowerCase());
    const words = search.trim().toLowerCase().split(/\s+/).filter(Boolean);
    return words.length === 0 || words.every((w) => fields.some((field) => field.includes(w)));
  });
  const totalPages = Math.max(1, Math.ceil(filtered.length / pageSize));
  const paged = filtered.slice().slice((page - 1) * pageSize, page * pageSize);

  const handleEdit = (r) => {
    setEditForm({
      ...r,
      formUuid: ensureFormUuid(r?.formUuid),
      isolationPossessionYesNo: isolationPossessionYesNoForSelect(
        r.isolationPossessionYesNo ?? r.isolationPossession
      ),
      inspectionItems: ensureInspectionItems(r.inspectionItems),
      actionsTable: ensureActionsTable(r.actionsTable),
      photos: normalizePhotos(r.photos)
    });
    setShowEditModal(true);
  };

  const handleEditSave = async () => {
    if (!editForm?._id) return;
    try {
      const token = localStorage.getItem("token");
      const body = {
        projectLocation: editForm.projectLocation,
        isolationPossessionYesNo: editForm.isolationPossessionYesNo,
        date: editForm.date,
        weekEnding: editForm.weekEnding,
        completedBy: editForm.completedBy,
        time: editForm.time,
        hseqAttendees: editForm.hseqAttendees,
        engineerAttendees: editForm.engineerAttendees,
        inspectionItems: editForm.inspectionItems,
        briefDescription: editForm.briefDescription,
        dayDiary: editForm.dayDiary,
        vehicles: editForm.vehicles,
        workingAtHeight: editForm.workingAtHeight,
        welfare: editForm.welfare,
        waste: editForm.waste,
        equipment: editForm.equipment,
        ppe: editForm.ppe,
        toolboxTalksBriefed: editForm.toolboxTalksBriefed,
        actionsTable: editForm.actionsTable,
        photos: normalizePhotos(editForm.photos),
        workforceConsultation: editForm.workforceConsultation,
        hseqRepresentative: editForm.hseqRepresentative,
        signOffDate: editForm.signOffDate,
        formUuid: ensureFormUuid(editForm.formUuid)
      };
      const res = await fetch(getBaseUrl() + "/api/rail-hseq-site-inspection/" + editForm._id, {
        method: "PUT",
        headers: { "Content-Type": "application/json", Authorization: `Bearer ${token}` },
        body: JSON.stringify(body)
      });
      const data = await res.json();
      if (res.ok && data.success) {
        await fetchRecords();
        setShowEditModal(false);
        setEditForm(null);
        toast.success("SF-015-C Rail HSEQ Site Inspection (2) updated!");
      } else {
        toast.error(data.message || "Failed to update");
      }
    } catch {
      toast.error("Failed to update");
    }
  };

  const handleDelete = (id) => {
    setDeleteId(id);
    setShowDeleteModal(true);
  };

  const confirmDelete = async () => {
    if (!deleteId) return;
    try {
      const token = localStorage.getItem("token");
      const res = await fetch(getBaseUrl() + "/api/rail-hseq-site-inspection/" + deleteId, {
        method: "DELETE",
        headers: { Authorization: `Bearer ${token}` }
      });
      const data = await res.json();
      if (res.ok && data.success) {
        setRecords(records.filter((f) => f._id !== deleteId));
        toast.success("Record deleted!");
      } else toast.error(data.message || "Failed to delete");
    } catch {
      toast.error("Failed to delete");
    }
    setShowDeleteModal(false);
    setDeleteId(null);
  };

  const formatDate = (d) => {
    if (!d) return "—";
    const x = new Date(d);
    return isNaN(x.getTime()) ? String(d) : x.toLocaleDateString("en-GB", { day: "2-digit", month: "short", year: "numeric" });
  };

  const getUserName = (record) => {
    const uid = record.userId;
    const u = appUsers.find((x) => x._id === uid);
    if (u) {
      const full = `${u.firstName || ""} ${u.lastName || ""}`.trim();
      return full || uid || "—";
    }
    return uid || "—";
  };

  const drawPdfSectionHeader = (doc, pageWidth, yPosition, number, title) => {
    doc.setFillColor(139, 22, 52);
    doc.setTextColor(255, 255, 255);
    doc.setFont("helvetica", "bold");
    doc.rect(10, yPosition, pageWidth - 20, 8, "F");
    doc.text(String(number), 14, yPosition + 5.5);
    doc.text(title, 22, yPosition + 5.5);
    return yPosition + 8;
  };

  const drawPdfInspectionTable = (doc, tableCommon, startY, rows, tableWidth, marginLeft) => {
    const insCol0 = 8;
    const insCol2 = 16;
    const insCol1 = tableWidth - insCol0 - insCol2;
    autoTable(doc, {
      ...tableCommon,
      startY,
      margin: { left: marginLeft, right: 10, bottom: 30 },
      tableWidth,
      head: [
        [
          { content: "No", styles: { fillColor: [240, 240, 240], fontStyle: "bold", halign: "center" } },
          { content: "Inspected Item", styles: { fillColor: [240, 240, 240], fontStyle: "bold" } },
          { content: "Suitable Y/N/NA", styles: { fillColor: [240, 240, 240], fontStyle: "bold", halign: "center" } }
        ]
      ],
      body: rows.map((row) => [
        { content: String(row.no), styles: { halign: "center" } },
        { content: row.item || "", styles: { halign: "left" } },
        { content: row.suitable || "", styles: { halign: "center" } }
      ]),
      columnStyles: {
        0: { cellWidth: insCol0 },
        1: { cellWidth: insCol1 },
        2: { cellWidth: insCol2 }
      },
      headStyles: {
        ...tableCommon.headStyles,
        fontSize: 6,
        cellPadding: 2
      },
      bodyStyles: {
        ...tableCommon.bodyStyles,
        fontSize: 6,
        cellPadding: 2
      },
      styles: { ...tableCommon.styles, fontSize: 6, cellPadding: 2 }
    });
    return doc.lastAutoTable.finalY;
  };

  const generatePDF = async (record, options = {}) => {
    const shouldUploadToSharePoint = options.uploadOnly === true;
    const uploadConfig = shouldUploadToSharePoint
      ? {
          uploadContext: options.uploadContext || "",
          onSuccess: options.onSuccess,
          onError: options.onError
        }
      : null;

    const doc = new jsPDF();
    const pageWidth = doc.internal.pageSize.getWidth();
    const pageHeight = doc.internal.pageSize.getHeight();
    const primaryColor = [139, 22, 52];

    const drawRoundedRect = (x, y, width, height, radius, fillColor, strokeColor, hasShadow = false) => {
      if (hasShadow) {
        doc.setFillColor(0, 0, 0, 0.1);
        doc.roundedRect(x + 1, y + 1, width, height, radius, radius, "F");
      }
      doc.setFillColor(...fillColor);
      doc.setDrawColor(...strokeColor);
      doc.setLineWidth(0.5);
      doc.roundedRect(x, y, width, height, radius, radius, "FD");
    };

    const addSignatureToPDF = async (signatureData, x, y, maxWidth, maxHeight) => {
      const signatureSrc = normalizeImageSource(signatureData, "image/png");
      if (!signatureSrc) return;

      try {
        await new Promise((resolve) => {
          const sigImg = new Image();
          sigImg.crossOrigin = "anonymous";
          sigImg.onload = () => {
            try {
              const aspectRatio = sigImg.width / sigImg.height;
              let imgWidth = maxWidth;
              let imgHeight = maxWidth / aspectRatio;
              if (imgHeight > maxHeight) {
                imgHeight = maxHeight;
                imgWidth = maxHeight * aspectRatio;
              }
              imgWidth = Math.min(imgWidth, maxWidth);
              imgHeight = Math.min(imgHeight, maxHeight);
              const scale = 4;
              const canvas = document.createElement("canvas");
              canvas.width = Math.round(imgWidth * scale);
              canvas.height = Math.round(imgHeight * scale);
              const ctx = canvas.getContext("2d");
              ctx.imageSmoothingEnabled = true;
              ctx.imageSmoothingQuality = "high";
              ctx.fillStyle = "#FFFFFF";
              ctx.fillRect(0, 0, canvas.width, canvas.height);
              ctx.drawImage(sigImg, 0, 0, sigImg.width, sigImg.height, 0, 0, canvas.width, canvas.height);
              const highResDataUrl = canvas.toDataURL("image/png", 1.0);
              doc.addImage(highResDataUrl, "PNG", x, y, imgWidth, imgHeight, "", "NONE");
            } catch {
              try {
                const signatureType = getPdfImageType(signatureSrc, "PNG");
                doc.addImage(signatureSrc, signatureType, x, y, maxWidth, maxHeight, "", "NONE");
              } catch {}
            }
            resolve();
          };
          sigImg.onerror = () => resolve();
          sigImg.src = signatureSrc;
        });
      } catch {}
    };

    const headerHeight = 30;
    drawRoundedRect(0, 0, pageWidth, headerHeight, 0, primaryColor, primaryColor);

    const addLogoToPDF = () => {
      return new Promise((resolve) => {
        const logoImg = new Image();
        logoImg.crossOrigin = "anonymous";
        logoImg.onload = function () {
          try {
            const logoWidth = 70;
            const aspectRatio = logoImg.height / logoImg.width;
            const logoHeight = logoWidth * aspectRatio;
            const logoX = 10;
            const logoY = (headerHeight - logoHeight) / 2;
            const canvas = document.createElement("canvas");
            const scale = 2.5;
            canvas.width = logoImg.width * scale;
            canvas.height = logoImg.height * scale;
            const ctx = canvas.getContext("2d");
            ctx.scale(scale, scale);
            ctx.drawImage(logoImg, 0, 0);
            const highResDataUrl = canvas.toDataURL("image/png", 1.0);
            doc.addImage(highResDataUrl, "PNG", logoX, logoY, logoWidth, logoHeight, "", "NONE");
          } catch {
            doc.setFillColor(255, 255, 255);
            doc.rect(10, 6, 60, 15, "F");
            doc.setFont("helvetica", "bold");
            doc.setFontSize(10);
            doc.setTextColor(0, 0, 0);
            doc.text("TAYLOR TECHNOLOGY", 15, 12);
            doc.text("SYSTEMS LIMITED", 15, 17);
          }
          resolve();
        };
        logoImg.onerror = function () {
          doc.setFillColor(255, 255, 255);
          doc.rect(10, 6, 60, 15, "F");
          doc.setFont("helvetica", "bold");
          doc.setFontSize(10);
          doc.setTextColor(0, 0, 0);
          doc.text("TAYLOR TECHNOLOGY", 15, 12);
          doc.text("SYSTEMS LIMITED", 15, 17);
          resolve();
        };
        logoImg.src = onsiteproLogo;
      });
    };

    await addLogoToPDF();

    const titleX = 90;
    doc.setFont("helvetica", "bold");
    doc.setFontSize(16);
    doc.setTextColor(255, 255, 255);
    doc.text("SF-015-C Rail HSEQ Site Inspection (2)", titleX, 13);
    doc.setFont("helvetica", "normal");
    doc.setFontSize(8);
    doc.text("TAYLOR TECHNOLOGY SYSTEMS LIMITED", titleX, 19);

    let yPosition = headerHeight + 6;
    doc.setTextColor(0, 0, 0);
    doc.setFont("helvetica", "bold");
    doc.setFontSize(14);
    doc.text("Rail HSQE Site Inspection", pageWidth / 2, yPosition + 6, { align: "center" });
    yPosition += 14;

    const formattedDate = formatCellDate(record.date);
    const formattedWeekEnding = formatCellDate(record.weekEnding);
    const formattedSignOffDate = formatCellDate(record.signOffDate);
    const addedBy =
      record.addedByFirstName || record.addedByLastName
        ? `${record.addedByFirstName || ""} ${record.addedByLastName || ""}`.trim()
        : getUserName(record);

    const innerTableW = pageWidth - 20;
    const tableCommon = {
      styles: {
        font: "helvetica",
        fontSize: 9,
        cellPadding: 3,
        overflow: "linebreak",
        valign: "middle"
      },
      theme: "grid",
      tableWidth: innerTableW,
      margin: { left: 10, right: 10, bottom: 30 },
      headStyles: {
        fillColor: [245, 245, 245],
        textColor: [0, 0, 0],
        lineWidth: 0.2
      },
      bodyStyles: {
        lineWidth: 0.2,
        textColor: [0, 0, 0]
      }
    };

    yPosition = drawPdfSectionHeader(doc, pageWidth, yPosition, "1", "Basic Information");

    autoTable(doc, {
      ...tableCommon,
      startY: yPosition,
      columnStyles: {
        0: { cellWidth: 48 },
        1: { cellWidth: 47 },
        2: { cellWidth: 48 },
        3: { cellWidth: 47 }
      },
      head: [
        [
          { content: "Project Location:", styles: { fillColor: [230, 230, 230], fontStyle: "bold" } },
          { content: record.projectLocation || "", styles: { halign: "center" } },
          { content: "Isolation/Possession:", styles: { fillColor: [230, 230, 230], fontStyle: "bold", halign: "center" } },
          { content: record.isolationPossessionYesNo || "", styles: { halign: "center" } }
        ]
      ],
      body: [
        [
          { content: "Date:", styles: { fillColor: [230, 230, 230], fontStyle: "bold" } },
          { content: formattedDate, styles: { halign: "center" } },
          { content: "Week Ending:", styles: { fillColor: [230, 230, 230], fontStyle: "bold", halign: "center" } },
          { content: formattedWeekEnding, styles: { halign: "center" } }
        ],
        [
          { content: "Completed by:", styles: { fillColor: [230, 230, 230], fontStyle: "bold" } },
          { content: record.completedBy || "", styles: { halign: "center" } },
          { content: "Time:", styles: { fillColor: [230, 230, 230], fontStyle: "bold", halign: "center" } },
          { content: record.time || "", styles: { halign: "center" } }
        ],
        [
          { content: "HSEQ Attendees:", styles: { fillColor: [230, 230, 230], fontStyle: "bold" } },
          { content: record.hseqAttendees || "", styles: { halign: "center" } },
          { content: "Added by:", styles: { fillColor: [230, 230, 230], fontStyle: "bold", halign: "center" } },
          { content: addedBy, styles: { halign: "center" } }
        ],
        [
          { content: "Engineer Attendees:", styles: { fillColor: [230, 230, 230], fontStyle: "bold" } },
          { content: record.engineerAttendees || "", styles: { halign: "center" } },
          { content: "", styles: { fillColor: [230, 230, 230] } },
          { content: "", styles: {} }
        ]
      ]
    });
    yPosition = doc.lastAutoTable.finalY + 2;

    const inspectionItems = ensureInspectionItems(record.inspectionItems);
    const safetyLeft = inspectionItems.filter((r) => r.no >= 1 && r.no <= 10);
    const safetyRight = inspectionItems.filter((r) => r.no >= 11 && r.no <= 19);
    const environmental = inspectionItems.filter((r) => r.section === "ENVIRONMENTAL");
    const other = inspectionItems.filter((r) => r.section === "OTHER");

    if (yPosition + 50 > pageHeight - 30) {
      doc.addPage();
      yPosition = 20;
    }
    yPosition = drawPdfSectionHeader(doc, pageWidth, yPosition, "2", "Inspection Items — SAFETY");

    const colGap = 4;
    const halfW = (innerTableW - colGap) / 2;
    const leftStartY = yPosition;
    drawPdfInspectionTable(doc, tableCommon, leftStartY, safetyLeft, halfW, 10);
    const leftEndY = doc.lastAutoTable.finalY;
    drawPdfInspectionTable(doc, tableCommon, leftStartY, safetyRight, halfW, 10 + halfW + colGap);
    const rightEndY = doc.lastAutoTable.finalY;
    yPosition = Math.max(leftEndY, rightEndY) + 2;

    const drawSectionTable = (sectionLabel, rows, sectionNum) => {
      if (yPosition + 40 > pageHeight - 30) {
        doc.addPage();
        yPosition = 20;
      }
      yPosition = drawPdfSectionHeader(doc, pageWidth, yPosition, sectionNum, `Inspection Items — ${sectionLabel}`);
      autoTable(doc, {
        ...tableCommon,
        startY: yPosition,
        head: [
          [
            { content: "No", styles: { fillColor: [240, 240, 240], fontStyle: "bold", halign: "center" } },
            { content: "Inspected Item", styles: { fillColor: [240, 240, 240], fontStyle: "bold" } },
            { content: "Suitable Y/N/NA", styles: { fillColor: [240, 240, 240], fontStyle: "bold", halign: "center" } }
          ]
        ],
        body: rows.map((row) => [
          { content: String(row.no), styles: { halign: "center" } },
          { content: row.item || "", styles: { halign: "left" } },
          { content: row.suitable || "", styles: { halign: "center" } }
        ]),
        columnStyles: {
          0: { cellWidth: 12 },
          1: { cellWidth: innerTableW - 44 },
          2: { cellWidth: 32 }
        },
        headStyles: { ...tableCommon.headStyles, fontSize: 8, cellPadding: 3 },
        bodyStyles: { ...tableCommon.bodyStyles, fontSize: 8, cellPadding: 3 },
        styles: { ...tableCommon.styles, fontSize: 8, cellPadding: 3 }
      });
      yPosition = doc.lastAutoTable.finalY + 2;
    };

    drawSectionTable("ENVIRONMENTAL", environmental, "3");
    drawSectionTable("OTHER", other, "4");

    if (yPosition + 50 > pageHeight - 30) {
      doc.addPage();
      yPosition = 20;
    }
    const narrativeSectionColW = 68;
    const narrativeBody = buildNarrativePdfSectionRows(record).map((row) => [
      {
        content: `${row.num}. ${row.title}`,
        styles: {
          fillColor: [245, 245, 245],
          fontStyle: "bold",
          fontSize: 8,
          valign: "middle"
        }
      },
      {
        content: formatNarrativePdfValue(row.value),
        styles: {
          fontSize: 9,
          valign: "top",
          minCellHeight: 11
        }
      }
    ]);
    autoTable(doc, {
      ...tableCommon,
      startY: yPosition,
      showHead: "everyPage",
      head: [
        [
          {
            content: "Section",
            styles: {
              fillColor: [139, 22, 52],
              textColor: [255, 255, 255],
              fontStyle: "bold",
              halign: "left"
            }
          },
          {
            content: "Details",
            styles: {
              fillColor: [139, 22, 52],
              textColor: [255, 255, 255],
              fontStyle: "bold",
              halign: "left"
            }
          }
        ]
      ],
      body: narrativeBody,
      columnStyles: {
        0: { cellWidth: narrativeSectionColW },
        1: { cellWidth: innerTableW - narrativeSectionColW }
      },
      headStyles: {
        ...tableCommon.headStyles,
        fontSize: 9,
        cellPadding: 4
      },
      bodyStyles: {
        ...tableCommon.bodyStyles,
        fontSize: 9,
        cellPadding: 4,
        overflow: "linebreak",
        valign: "top"
      },
      styles: {
        ...tableCommon.styles,
        fontSize: 9,
        cellPadding: 4,
        overflow: "linebreak",
        valign: "top"
      }
    });
    yPosition = doc.lastAutoTable.finalY + 2;

    let narrativeSectionNum = 14;

    const actionsRows = ensureActionsTable(record.actionsTable);
    if (yPosition + 50 > pageHeight - 30) {
      doc.addPage();
      yPosition = 20;
    }
    yPosition = drawPdfSectionHeader(doc, pageWidth, yPosition, String(narrativeSectionNum), "Actions");
    narrativeSectionNum += 1;
    autoTable(doc, {
      ...tableCommon,
      startY: yPosition,
      head: [
        [
          { content: "Ref", styles: { fillColor: [240, 240, 240], fontStyle: "bold", halign: "center" } },
          { content: "Good Practice or Corrective Action", styles: { fillColor: [240, 240, 240], fontStyle: "bold" } },
          { content: "Responsibility", styles: { fillColor: [240, 240, 240], fontStyle: "bold", halign: "center" } },
          { content: "Action Complete", styles: { fillColor: [240, 240, 240], fontStyle: "bold", halign: "center" } }
        ]
      ],
      body: actionsRows.map((row) => [
        { content: row.ref || "", styles: { halign: "center" } },
        { content: row.goodPracticeOrCorrectiveAction || "", styles: { halign: "left" } },
        { content: row.responsibility || "", styles: { halign: "center" } },
        { content: row.actionComplete || "", styles: { halign: "center" } }
      ]),
      columnStyles: {
        0: { cellWidth: 18 },
        1: { cellWidth: innerTableW - 18 - 40 - 40 },
        2: { cellWidth: 40 },
        3: { cellWidth: 40 }
      },
      headStyles: { ...tableCommon.headStyles, fontSize: 8 },
      bodyStyles: { ...tableCommon.bodyStyles, fontSize: 8 },
      styles: { ...tableCommon.styles, fontSize: 8 }
    });
    yPosition = doc.lastAutoTable.finalY + 2;

    if (yPosition + 90 > pageHeight - 30) {
      doc.addPage();
      yPosition = 20;
    }
    yPosition = drawPdfSectionHeader(doc, pageWidth, yPosition, String(narrativeSectionNum), "Sign-off");
    doc.setTextColor(0, 0, 0);
    doc.setFont("helvetica", "normal");
    doc.setFontSize(9);
    const wf = pdfSafeText(record.workforceConsultation);
    const wfLines = doc.splitTextToSize(`Workforce consultation: ${wf}`, pageWidth - 24);
    doc.text(wfLines, 12, yPosition + 6);
    yPosition += 6 + wfLines.length * 4.8;
    const hseqRep = pdfSafeText(record.hseqRepresentative);
    const hseqRepLines = doc.splitTextToSize(`HSEQ Representative: ${hseqRep}`, pageWidth - 24);
    doc.text(hseqRepLines, 12, yPosition + 4);
    yPosition += 4 + hseqRepLines.length * 4.8;
    doc.text(`Sign-off Date: ${formattedSignOffDate || "—"}`, 12, yPosition + 4);
    yPosition += 14;

    const blockW = pageWidth - 40;
    const blockH = 34;
    doc.setDrawColor(200, 200, 200);
    doc.setFillColor(248, 248, 248);
    doc.roundedRect(20, yPosition, blockW, blockH, 3, 3, "FD");
    doc.setTextColor(139, 22, 52);
    doc.setFont("helvetica", "bold");
    doc.setFontSize(9);
    doc.text("HSEQ Representative Signature", 24, yPosition + 6.5);
    const signatureSrc = normalizeImageSource(record.signature, "image/png");
    if (signatureSrc) {
      await addSignatureToPDF(record.signature, 24, yPosition + 9, blockW - 8, blockH - 12);
    } else {
      doc.setFont("helvetica", "normal");
      doc.setTextColor(120, 120, 120);
      doc.text("—", 24, yPosition + 19);
    }
    yPosition += blockH + 8;

    // Inspection Notes. The item grids above are drawn two-up, which leaves no room for
    // a comments column, so the notes are listed here in full width instead — only the
    // rows that carry one. A form with no notes renders exactly as it did before.
    const inspectionNotes = getInspectionNotes(inspectionItems);
    if (inspectionNotes.length) {
      narrativeSectionNum += 1;
      if (yPosition + 40 > pageHeight - 30) {
        doc.addPage();
        yPosition = 20;
      }
      yPosition = drawPdfSectionHeader(
        doc,
        pageWidth,
        yPosition,
        String(narrativeSectionNum),
        `Inspection Notes (${inspectionNotes.length})`
      );
      const notesSuitableW = 24;
      const notesNoW = 12;
      const notesItemW = 58;
      autoTable(doc, {
        ...tableCommon,
        startY: yPosition,
        showHead: "everyPage",
        head: [
          [
            { content: "No", styles: { fillColor: [240, 240, 240], fontStyle: "bold", halign: "center" } },
            { content: "Inspected Item", styles: { fillColor: [240, 240, 240], fontStyle: "bold" } },
            { content: "Suitable", styles: { fillColor: [240, 240, 240], fontStyle: "bold", halign: "center" } },
            { content: "Notes", styles: { fillColor: [240, 240, 240], fontStyle: "bold" } }
          ]
        ],
        body: inspectionNotes.map((row) => [
          { content: String(row.no), styles: { halign: "center" } },
          { content: row.item || "", styles: { halign: "left" } },
          { content: row.suitable || "", styles: { halign: "center" } },
          { content: pdfSafeText(row.comments), styles: { halign: "left" } }
        ]),
        columnStyles: {
          0: { cellWidth: notesNoW },
          1: { cellWidth: notesItemW },
          2: { cellWidth: notesSuitableW },
          3: { cellWidth: innerTableW - notesNoW - notesItemW - notesSuitableW }
        },
        headStyles: { ...tableCommon.headStyles, fontSize: 8, cellPadding: 3 },
        bodyStyles: { ...tableCommon.bodyStyles, fontSize: 8, cellPadding: 3 },
        styles: { ...tableCommon.styles, fontSize: 8, cellPadding: 3, overflow: "linebreak", valign: "top" }
      });
      yPosition = doc.lastAutoTable.finalY + 4;
    }

    const photoList = normalizePhotos(record.photos);
    if (photoList.length) {
      narrativeSectionNum += 1;
      const cols = 2;
      const gap = 6;
      const marginX = 12;
      const contentW = pageWidth - 24;
      const cellW = (contentW - (cols - 1) * gap) / cols;
      const cellH = 52;

      if (yPosition + 30 > pageHeight - 30) {
        doc.addPage();
        yPosition = 20;
      }
      yPosition = drawPdfSectionHeader(
        doc,
        pageWidth,
        yPosition,
        String(narrativeSectionNum),
        `Photos (${photoList.length})`
      );
      yPosition += 4;

      let rowStartY = yPosition;
      for (let i = 0; i < photoList.length; i += 1) {
        const col = i % cols;
        if (col === 0 && i > 0) {
          rowStartY += cellH + gap;
        }
        if (col === 0 && rowStartY + cellH > pageHeight - 30) {
          doc.addPage();
          rowStartY = 20;
        }
        const x = marginX + col * (cellW + gap);
        doc.setDrawColor(220, 220, 220);
        doc.setFillColor(255, 255, 255);
        doc.roundedRect(x, rowStartY, cellW, cellH, 2, 2, "FD");
        try {
          const photoType = getPdfImageType(photoList[i], "JPEG");
          doc.addImage(photoList[i], photoType, x + 2, rowStartY + 2, cellW - 4, cellH - 4, "", "NONE");
        } catch {}
      }
      yPosition = rowStartY + cellH + 10;
    }

    const totalPages = doc.getNumberOfPages();
    for (let p = 1; p <= totalPages; p++) {
      doc.setPage(p);
      const footerY = pageHeight - 25;
      drawRoundedRect(0, footerY, pageWidth, 25, 0, [248, 249, 250], [200, 200, 200]);
      doc.setFont("helvetica", "normal");
      doc.setFontSize(8);
      doc.setTextColor(150, 150, 150);
      doc.text(`Page ${p} of ${totalPages}`, pageWidth - 35, footerY + 12);
    }

    const safeLocation = String(record.projectLocation || "Location").replace(/[\\/:"*?<>|]+/g, "-");
    const safeDate = String(formattedDate || "inspection").replace(/[\\/:"*?<>|]+/g, "-");
    let fileName;
    if (shouldUploadToSharePoint && uploadConfig) {
      fileName = buildSharePointFileName([
        addedByName(record),
        record.projectLocation,
        toDdMmYyyy(record.date),
      ]);
    } else {
      fileName = `SF015C_Issue2_${safeLocation}_${safeDate}.pdf`;
    }

    if (shouldUploadToSharePoint && uploadConfig) {
      try {
        const key = `${record._id || ""}|${record.projectLocation || ""}|${record.date || ""}`;
        setUploadingKey(key);
        const apiUrl = (process.env.REACT_APP_API_URL || "").replace(/\/$/, "");
        const dataUri = doc.output("datauristring");
        const uploadParams = {
          fileName,
          fileBase64: dataUri,
          uploadContext: uploadConfig.uploadContext, uploadSubFolder: "SF-015-C Rail HSEQ Site Inspection"
        };
        const res = await fetch(`${apiUrl}/api/sharepoint/upload`, {
          method: "POST",
          headers: { "Content-Type": "application/json", Authorization: `Bearer ${localStorage.getItem("token")}` },
          body: JSON.stringify(uploadParams)
        });
        if (res.status !== 204 && !res.ok) {
          let msg = "SharePoint upload failed";
          try {
            const out = await res.json();
            msg = out?.message || msg;
          } catch {}
          throw new Error(msg);
        }
        toast.success("PDF uploaded to SharePoint successfully!");
        if (uploadConfig.onSuccess) uploadConfig.onSuccess();
      } catch (err) {
        toast.error(err?.message || "SharePoint upload failed");
        if (uploadConfig.onError) uploadConfig.onError(err);
      } finally {
        setUploadingKey(null);
      }
    } else {
      doc.save(fileName);
      toast.success("SF-015-C Rail HSEQ Site Inspection (2) PDF generated successfully!");
    }
  };

  const updateInspectionRowByNo = (no, patch) => {
    setEditForm((prev) => {
      if (!prev) return prev;
      const next = [...(prev.inspectionItems || [])];
      const index = next.findIndex((r) => r.no === no);
      if (index < 0) return prev;
      next[index] = { ...next[index], ...patch };
      return { ...prev, inspectionItems: next };
    });
  };

  const handlePhotoSelect = async (e) => {
    const files = Array.from(e.target.files || []);
    if (!files.length) return;
    const imageFiles = files.filter((f) => f.type.startsWith("image/"));
    if (!imageFiles.length) {
      toast.error("Please select image files.");
      setPhotoInputResetKey((k) => k + 1);
      return;
    }
    try {
      const dataUrls = await Promise.all(imageFiles.map((file) => fileToDataUrl(file)));
      const valid = dataUrls.filter(Boolean);
      if (!valid.length) {
        toast.error("Failed to load images.");
        return;
      }
      setEditForm((prev) =>
        prev
          ? {
              ...prev,
              photos: [...normalizePhotos(prev.photos), ...valid]
            }
          : prev
      );
    } catch {
      toast.error("Failed to load images.");
    } finally {
      setPhotoInputResetKey((k) => k + 1);
    }
  };

  const removePhotoAtIndex = (index) => {
    setEditForm((prev) => {
      if (!prev) return prev;
      const next = normalizePhotos(prev.photos).filter((_, idx) => idx !== index);
      return { ...prev, photos: next };
    });
  };

  const updateActionsRow = (index, patch) => {
    setEditForm((prev) => {
      if (!prev) return prev;
      const next = [...(prev.actionsTable || [])];
      next[index] = { ...next[index], ...patch };
      return { ...prev, actionsTable: next };
    });
  };

  const addActionsRow = () => {
    setEditForm((prev) => {
      if (!prev) return prev;
      return {
        ...prev,
        actionsTable: [
          ...(prev.actionsTable || []),
          { ref: "", goodPracticeOrCorrectiveAction: "", responsibility: "", actionComplete: "" }
        ]
      };
    });
  };

  const removeActionsRow = (index) => {
    setEditForm((prev) => {
      if (!prev) return prev;
      const next = (prev.actionsTable || []).filter((_, i) => i !== index);
      return {
        ...prev,
        actionsTable: next.length ? next : [{ ref: "", goodPracticeOrCorrectiveAction: "", responsibility: "", actionComplete: "" }]
      };
    });
  };

  const renderSection = (title, children) => (
    <div style={{ marginBottom: 20, padding: 16, border: "1px solid #e2e8f0", borderRadius: 12, background: "#f8fafc" }}>
      <div style={{ fontWeight: 700, color: THEME, marginBottom: 12, fontSize: 13 }}>{title}</div>
      {children}
    </div>
  );

  const tableCellStyle = { border: "1px solid #d1d5db", padding: "6px 8px", fontSize: 11, verticalAlign: "middle" };
  const tableHeaderStyle = { ...tableCellStyle, fontWeight: 600, color: THEME, background: "#fafafa" };

  const renderInspectionMiniTable = (rows) => (
    <table style={{ width: "100%", borderCollapse: "collapse", border: "1px solid #d1d5db" }}>
      <thead>
        <tr>
          <th style={tableHeaderStyle}>No</th>
          <th style={{ ...tableHeaderStyle, minWidth: 120 }}>Inspected Item</th>
          <th style={tableHeaderStyle}>Suitable Y/N/NA</th>
        </tr>
      </thead>
      <tbody>
        {rows.map((row) => (
          <tr key={row.no}>
            <td style={tableCellStyle}>{row.no}</td>
            <td style={tableCellStyle}>{row.item}</td>
            <td style={tableCellStyle}>
              <ThemeSelect
                compact
                value={row.suitable}
                onChange={(val) => updateInspectionRowByNo(row.no, { suitable: val })}
                options={SUITABLE_OPTIONS}
              />
            </td>
          </tr>
        ))}
      </tbody>
    </table>
  );

  const safetyLeftRows = (editForm?.inspectionItems || []).filter((r) => r.no >= 1 && r.no <= 10);
  const safetyRightRows = (editForm?.inspectionItems || []).filter((r) => r.no >= 11 && r.no <= 19);
  const environmentalRows = (editForm?.inspectionItems || []).filter((r) => r.section === "ENVIRONMENTAL");
  const otherRows = (editForm?.inspectionItems || []).filter((r) => r.section === "OTHER");
  const inspectionNoteCount = getInspectionNotes(editForm?.inspectionItems).length;

  return (
    <>
      <div style={{ background: "#fff", borderRadius: 20, boxShadow: "0px 4px 20px rgba(0,0,0,0.08)", padding: 24, marginBottom: 24 }}>
        <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 24, flexWrap: "wrap", gap: 16 }}>
          <h2 style={{ fontSize: 24, fontWeight: 700, color: "#1a202c", margin: 0 }}>SF-015-C Rail HSEQ Site Inspection (2)</h2>
          <div style={{ display: "flex", alignItems: "center", gap: 12, flexWrap: "wrap" }}>
            {isAdminUser && (
              <button
                type="button"
                onClick={() => setShowImportModal(true)}
                style={{
                  background: "#8b1634",
                  color: "#fff",
                  border: "none",
                  borderRadius: "10px",
                  padding: "10px 14px",
                  fontWeight: "600",
                  fontSize: "13px",
                  cursor: "pointer"
                }}
              >
                Import File
              </button>
            )}
            <div style={{ position: "relative", display: "flex", alignItems: "center" }}>
              <i className="fa fa-search" style={{ position: "absolute", left: 12, color: THEME, fontSize: 14, pointerEvents: "none", zIndex: 1 }} />
              <input
                type="text"
                placeholder="Type to Search"
                value={search}
                onChange={(e) => {
                  setSearch(e.target.value);
                  setPage(1);
                }}
                style={{
                  padding: "10px 12px 10px 36px",
                  borderRadius: 12,
                  border: "1px solid #e2e8f0",
                  fontSize: 14,
                  outline: "none",
                  width: 250,
                  boxSizing: "border-box"
                }}
                onFocus={(e) => (e.target.style.borderColor = THEME)}
                onBlur={(e) => (e.target.style.borderColor = "#e2e8f0")}
              />
            </div>
          </div>
        </div>
        <div style={{ overflowX: "auto", borderRadius: 12, border: "1px solid #e2e8f0" }}>
          <table style={{ width: "100%", borderCollapse: "collapse" }}>
            <thead>
              <tr style={{ background: "#f7fafc", borderBottom: "1px solid #e2e8f0" }}>
                <th style={{ fontWeight: 600, color: THEME, fontSize: 14, padding: "12px 16px", textAlign: "left" }}>Project Location</th>
                <th style={{ fontWeight: 600, color: THEME, fontSize: 14, padding: "12px 16px", textAlign: "left" }}>Date</th>
                <th style={{ fontWeight: 600, color: THEME, fontSize: 14, padding: "12px 16px", textAlign: "left" }}>Week Ending</th>
                <th style={{ fontWeight: 600, color: THEME, fontSize: 14, padding: "12px 16px", textAlign: "left" }}>Time</th>
                <th style={{ fontWeight: 600, color: THEME, fontSize: 14, padding: "12px 16px", textAlign: "left" }}>Completed by</th>
                <th style={{ fontWeight: 600, color: THEME, fontSize: 14, padding: "12px 16px", textAlign: "left" }}>Added By</th>
                <th style={{ fontWeight: 600, color: THEME, fontSize: 14, padding: "12px 16px", textAlign: "left" }}>Updated Date</th>
                <th style={{ fontWeight: 600, color: THEME, fontSize: 14, padding: "12px 16px", textAlign: "left" }}>Actions</th>
              </tr>
            </thead>
            <tbody>
              {loadingRecords ? (
                <tr>
                  <td colSpan={8} style={{ textAlign: "center", padding: 40, color: THEME, fontSize: 16, fontWeight: 600 }}>
                    <i className="fa fa-spinner fa-spin" style={{ marginRight: 8 }} />
                    Loading records...
                  </td>
                </tr>
              ) : paged.length === 0 ? (
                <tr>
                  <td colSpan={8} style={{ textAlign: "center", padding: 40, color: "#718096", fontSize: 16 }}>
                    No records found.
                  </td>
                </tr>
              ) : (
                paged.map((r, idx) => {
                  const updatedDate = r.updatedAt || r.createdAt;
                  return (
                    <tr key={r._id || idx} style={{ borderBottom: "1px solid #f1f5f9" }}>
                      <td style={{ color: "#2d3748", fontSize: 14, padding: 16 }}>{r.projectLocation || "—"}</td>
                      <td style={{ color: "#2d3748", fontSize: 14, padding: 16 }}>{formatCellDate(r.date)}</td>
                      <td style={{ color: "#2d3748", fontSize: 14, padding: 16 }}>{formatCellDate(r.weekEnding)}</td>
                      <td style={{ color: "#2d3748", fontSize: 14, padding: 16 }}>{r.time || "—"}</td>
                      <td style={{ color: "#2d3748", fontSize: 14, padding: 16 }}>{r.completedBy || "—"}</td>
                      <td style={{ color: "#2d3748", fontSize: 14, padding: 16 }}>{getUserName(r)}</td>
                      <td style={{ color: "#2d3748", fontSize: 14, padding: 16 }}>{formatDate(updatedDate)}</td>
                      <td style={{ padding: 16 }}>
                        <div style={{ display: "flex", gap: 8, alignItems: "center" }}>
                          {canEditDelete && (
                            <button type="button" onClick={() => handleEdit(r)} title="Edit" style={{ background: "transparent", border: "none", cursor: "pointer", padding: 6 }}>
                              <i className="fa fa-edit" style={{ color: "#22c55e", fontSize: 16 }} />
                            </button>
                          )}
                          <button
                            type="button"
                            onClick={() => generatePDF(r)}
                            title="Generate SF-015-C (2) PDF"
                            style={{ background: "transparent", border: "none", cursor: "pointer", padding: 6 }}
                          >
                            <i className="fa fa-file-pdf-o" style={{ color: "#dc2626", fontSize: 16 }} />
                          </button>
                          {(() => {
                            const k = `${r._id || ""}|${r.projectLocation || ""}|${r.date || ""}`;
                            const busy = uploadingKey === k;
                            return (
                              <button
                                type="button"
                                onClick={() => {
                                  if (!busy) {
                                    generatePDF(r, {
                                      uploadOnly: true,
                                      uploadContext: "hseq-forms-tablets"
                                    });
                                  }
                                }}
                                disabled={busy}
                                title={busy ? "Uploading..." : "Upload to SharePoint"}
                                style={{
                                  background: "transparent",
                                  border: "none",
                                  cursor: busy ? "not-allowed" : "pointer",
                                  padding: 6,
                                  opacity: busy ? 0.6 : 1
                                }}
                              >
                                <i className={busy ? "fa fa-spinner fa-spin" : "fa fa-upload"} style={{ color: "#22c55e", fontSize: 16 }} />
                              </button>
                            );
                          })()}
                          {canEditDelete && (
                            <button type="button" onClick={() => handleDelete(r._id)} title="Delete" style={{ background: "transparent", border: "none", cursor: "pointer", padding: 6 }}>
                              <i className="fa fa-trash" style={{ color: "#e53e3e", fontSize: 16 }} />
                            </button>
                          )}
                        </div>
                      </td>
                    </tr>
                  );
                })
              )}
            </tbody>
          </table>
        </div>
        <div style={{ display: "flex", justifyContent: "flex-end", alignItems: "center", marginTop: 18, gap: 12 }}>
          <button
            type="button"
            className="horizon-table-btn"
            disabled={page === 1}
            onClick={() => setPage(page - 1)}
            style={{
              padding: "8px 16px",
              borderRadius: 8,
              border: "1px solid #e2e8f0",
              background: page === 1 ? "#f7fafc" : "#fff",
              color: page === 1 ? "#a0aec0" : THEME,
              cursor: page === 1 ? "not-allowed" : "pointer"
            }}
          >
            Prev
          </button>
          <span style={{ fontWeight: 700, color: THEME, fontSize: 16 }}>
            {page} / {totalPages}
          </span>
          <button
            type="button"
            className="horizon-table-btn"
            disabled={page === totalPages}
            onClick={() => setPage(page + 1)}
            style={{
              padding: "8px 16px",
              borderRadius: 8,
              border: "1px solid #e2e8f0",
              background: page === totalPages ? "#f7fafc" : "#fff",
              color: page === totalPages ? "#a0aec0" : THEME,
              cursor: page === totalPages ? "not-allowed" : "pointer"
            }}
          >
            Next
          </button>
        </div>
      </div>

      {showEditModal && editForm && (
        <div className="modal fade show" style={{ display: "block", background: "rgba(0,0,0,0.3)" }} tabIndex="-1">
          <div className="modal-dialog modal-xl modal-dialog-centered modal-dialog-scrollable">
            <div className="modal-content" style={{ maxWidth: 1200, margin: "0 auto" }}>
              <div className="modal-header" style={{ background: THEME, color: "#fff" }}>
                <h5 className="modal-title">Edit SF-015-C Rail HSEQ Site Inspection (2)</h5>
                <button type="button" className="btn-close btn-close-white" onClick={() => setShowEditModal(false)} />
              </div>
              <div className="modal-body" style={{ maxHeight: "70vh", overflowY: "auto" }}>
                {renderSection("projectLocation · isolationPossessionYesNo · date · weekEnding · completedBy · time · attendees", (
                  <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 12 }}>
                    <div>
                      <label style={{ fontSize: 12, color: THEME, display: "block", marginBottom: 4 }}>projectLocation</label>
                      <input
                        type="text"
                        className="form-control form-control-sm"
                        value={editForm.projectLocation || ""}
                        onChange={(e) => setEditForm({ ...editForm, projectLocation: e.target.value })}
                        style={{ borderColor: THEME, borderRadius: 8 }}
                      />
                    </div>
                    <div>
                      <label style={{ fontSize: 12, color: THEME, display: "block", marginBottom: 4 }}>Isolation/Possession Yes/No</label>
                      <ThemeSelect
                        value={editForm.isolationPossessionYesNo || ""}
                        onChange={(val) => setEditForm({ ...editForm, isolationPossessionYesNo: val })}
                        options={YES_NO_OPTIONS}
                      />
                    </div>
                    <div>
                      <label style={{ fontSize: 12, color: THEME, display: "block", marginBottom: 4 }}>date</label>
                      <DatePicker
                        selected={parseStoredDate(editForm.date)}
                        onChange={(d) => setEditForm({ ...editForm, date: d ? dateToYmd(d) : "" })}
                        dateFormat="dd-MM-yyyy"
                        className="form-control form-control-sm"
                        style={{ width: "100%", fontSize: 12, borderColor: THEME, borderRadius: 8 }}
                        placeholderText="Select date"
                      />
                    </div>
                    <div>
                      <label style={{ fontSize: 12, color: THEME, display: "block", marginBottom: 4 }}>weekEnding</label>
                      <DatePicker
                        selected={parseStoredDate(editForm.weekEnding)}
                        onChange={(d) => setEditForm({ ...editForm, weekEnding: d ? dateToYmd(d) : "" })}
                        dateFormat="dd-MM-yyyy"
                        className="form-control form-control-sm"
                        style={{ width: "100%", fontSize: 12, borderColor: THEME, borderRadius: 8 }}
                        placeholderText="Select week ending"
                      />
                    </div>
                    <div>
                      <label style={{ fontSize: 12, color: THEME, display: "block", marginBottom: 4 }}>completedBy</label>
                      <input
                        type="text"
                        className="form-control form-control-sm"
                        value={editForm.completedBy || ""}
                        onChange={(e) => setEditForm({ ...editForm, completedBy: e.target.value })}
                        style={{ borderColor: THEME, borderRadius: 8 }}
                      />
                    </div>
                    <div>
                      <label style={{ fontSize: 12, color: THEME, display: "block", marginBottom: 4 }}>time</label>
                      <input
                        type="text"
                        className="form-control form-control-sm"
                        value={editForm.time || ""}
                        onChange={(e) => setEditForm({ ...editForm, time: e.target.value })}
                        style={{ borderColor: THEME, borderRadius: 8 }}
                      />
                    </div>
                    <div style={{ gridColumn: "1 / -1" }}>
                      <label style={{ fontSize: 12, color: THEME, display: "block", marginBottom: 4 }}>hseqAttendees</label>
                      <input
                        type="text"
                        className="form-control form-control-sm"
                        value={editForm.hseqAttendees || ""}
                        onChange={(e) => setEditForm({ ...editForm, hseqAttendees: e.target.value })}
                        style={{ borderColor: THEME, borderRadius: 8 }}
                      />
                    </div>
                    <div style={{ gridColumn: "1 / -1" }}>
                      <label style={{ fontSize: 12, color: THEME, display: "block", marginBottom: 4 }}>engineerAttendees</label>
                      <input
                        type="text"
                        className="form-control form-control-sm"
                        value={editForm.engineerAttendees || ""}
                        onChange={(e) => setEditForm({ ...editForm, engineerAttendees: e.target.value })}
                        style={{ borderColor: THEME, borderRadius: 8 }}
                      />
                    </div>
                  </div>
                ))}
                {renderSection("SAFETY", (
                  <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 16, overflowX: "auto" }}>
                    <div>{renderInspectionMiniTable(safetyLeftRows)}</div>
                    <div>{renderInspectionMiniTable(safetyRightRows)}</div>
                  </div>
                ))}
                {renderSection("ENVIRONMENTAL", (
                  <div style={{ overflowX: "auto" }}>{renderInspectionMiniTable(environmentalRows)}</div>
                ))}
                {renderSection("OTHER", (
                  <div style={{ overflowX: "auto" }}>{renderInspectionMiniTable(otherRows)}</div>
                ))}
                {renderSection(
                  `Inspection Notes${inspectionNoteCount ? ` (${inspectionNoteCount})` : ""}`,
                  <>
                    <div style={{ fontSize: 11, color: "#64748b", marginBottom: 10 }}>
                      Notes written against each inspected item. Only items with a note appear in the PDF.
                    </div>
                    <div style={{ display: "grid", gap: 8 }}>
                      {(editForm?.inspectionItems || []).map((row) => (
                        <div
                          key={row.no}
                          style={{ display: "grid", gridTemplateColumns: "minmax(140px, 220px) 1fr", gap: 10, alignItems: "start" }}
                        >
                          <div style={{ fontSize: 11, color: "#334155", paddingTop: 6 }}>
                            <span style={{ fontWeight: 600, color: THEME }}>{row.no}.</span> {row.item}
                          </div>
                          <textarea
                            className="form-control form-control-sm"
                            rows={1}
                            value={row.comments || ""}
                            onChange={(e) => updateInspectionRowByNo(row.no, { comments: e.target.value })}
                            style={{ borderColor: THEME, borderRadius: 8, fontSize: 12 }}
                          />
                        </div>
                      ))}
                    </div>
                  </>
                )}
                {renderSection(
                  "Brief description of works inspected, include comment on paperwork and welfare as well:",
                  <textarea
                    className="form-control form-control-sm"
                    rows={4}
                    value={editForm.briefDescription || ""}
                    onChange={(e) => setEditForm({ ...editForm, briefDescription: e.target.value })}
                    style={{ borderColor: THEME, borderRadius: 8, fontSize: 12 }}
                  />
                )}
                {NARRATIVE_FIELDS.map((field) => (
                  <div
                    key={field.key}
                    style={{ marginBottom: 20, padding: 16, border: "1px solid #e2e8f0", borderRadius: 12, background: "#f8fafc" }}
                  >
                    <div style={{ fontWeight: 700, color: THEME, marginBottom: 10, fontSize: 13 }}>{field.title}</div>
                    <textarea
                      className="form-control form-control-sm"
                      rows={4}
                      value={editForm[field.key] || ""}
                      onChange={(e) => setEditForm({ ...editForm, [field.key]: e.target.value })}
                      style={{ borderColor: THEME, borderRadius: 8, fontSize: 12 }}
                    />
                  </div>
                ))}
                {renderSection("actionsTable", (
                  <div style={{ overflowX: "auto" }}>
                    <table style={{ width: "100%", borderCollapse: "collapse", border: "1px solid #d1d5db", borderRadius: 8 }}>
                      <thead>
                        <tr>
                          <th style={tableHeaderStyle}>ref</th>
                          <th style={{ ...tableHeaderStyle, minWidth: 200 }}>goodPracticeOrCorrectiveAction</th>
                          <th style={tableHeaderStyle}>responsibility</th>
                          <th style={tableHeaderStyle}>actionComplete</th>
                          <th style={tableHeaderStyle} />
                        </tr>
                      </thead>
                      <tbody>
                        {(editForm.actionsTable || []).map((row, i) => (
                          <tr key={i}>
                            <td style={tableCellStyle}>
                              <input
                                type="text"
                                className="form-control form-control-sm"
                                value={row.ref || ""}
                                onChange={(e) => updateActionsRow(i, { ref: e.target.value })}
                                style={{ fontSize: 11 }}
                              />
                            </td>
                            <td style={tableCellStyle}>
                              <input
                                type="text"
                                className="form-control form-control-sm"
                                value={row.goodPracticeOrCorrectiveAction || ""}
                                onChange={(e) => updateActionsRow(i, { goodPracticeOrCorrectiveAction: e.target.value })}
                                style={{ fontSize: 11, minWidth: 180 }}
                              />
                            </td>
                            <td style={tableCellStyle}>
                              <input
                                type="text"
                                className="form-control form-control-sm"
                                value={row.responsibility || ""}
                                onChange={(e) => updateActionsRow(i, { responsibility: e.target.value })}
                                style={{ fontSize: 11 }}
                              />
                            </td>
                            <td style={tableCellStyle}>
                              <input
                                type="text"
                                className="form-control form-control-sm"
                                value={row.actionComplete || ""}
                                onChange={(e) => updateActionsRow(i, { actionComplete: e.target.value })}
                                style={{ fontSize: 11 }}
                              />
                            </td>
                            <td style={tableCellStyle}>
                              <button
                                type="button"
                                className="btn btn-sm btn-outline-danger"
                                onClick={() => removeActionsRow(i)}
                                disabled={(editForm.actionsTable || []).length <= 1}
                              >
                                Remove
                              </button>
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                    <button type="button" className="btn btn-sm mt-2" style={{ background: THEME, color: "#fff", fontWeight: 600 }} onClick={addActionsRow}>
                      Add row
                    </button>
                  </div>
                ))}
                {renderSection("workforceConsultation", (
                  <textarea
                    className="form-control form-control-sm"
                    rows={4}
                    value={editForm.workforceConsultation || ""}
                    onChange={(e) => setEditForm({ ...editForm, workforceConsultation: e.target.value })}
                    style={{ borderColor: THEME, borderRadius: 8, fontSize: 12 }}
                  />
                ))}
                <div style={{ marginBottom: 20, padding: 16, border: "1px solid #e2e8f0", borderRadius: 12, background: "#fff" }}>
                  <div style={{ fontWeight: 700, color: THEME, marginBottom: 10, fontSize: 13 }}>HSEQ Representative Sign-off</div>
                  <label style={{ fontSize: 12, color: THEME, display: "block", marginBottom: 4 }}>hseqRepresentative</label>
                  <input
                    type="text"
                    className="form-control form-control-sm"
                    value={editForm.hseqRepresentative || ""}
                    onChange={(e) => setEditForm({ ...editForm, hseqRepresentative: e.target.value })}
                    style={{ borderColor: THEME, borderRadius: 12, marginBottom: 12, height: 38 }}
                  />
                  <label style={{ fontSize: 12, color: THEME, display: "block", marginBottom: 4 }}>signOffDate</label>
                  <DatePicker
                    selected={parseStoredDate(editForm.signOffDate)}
                    onChange={(d) => setEditForm({ ...editForm, signOffDate: d ? dateToYmd(d) : "" })}
                    dateFormat="dd-MM-yyyy"
                    className="form-control form-control-sm"
                    style={{ width: "100%", borderColor: THEME, borderRadius: 12, marginBottom: 12, height: 38 }}
                    placeholderText="Select date"
                  />
                  <div style={{ fontSize: 12, fontWeight: 700, color: THEME, marginBottom: 6 }}>
                    photos{" "}
                    <span style={{ fontWeight: 400, color: "#666" }}>
                      ({normalizePhotos(editForm.photos).length} photo
                      {normalizePhotos(editForm.photos).length === 1 ? "" : "s"})
                    </span>
                  </div>
                  <div style={{ marginBottom: 12 }}>
                    <input
                      key={photoInputResetKey}
                      type="file"
                      accept="image/*"
                      multiple
                      className="form-control form-control-sm"
                      style={{ borderColor: THEME, borderRadius: 12, marginBottom: 8 }}
                      onChange={handlePhotoSelect}
                    />
                    {normalizePhotos(editForm.photos).length ? (
                      <div style={{ display: "flex", flexWrap: "wrap", gap: 8 }}>
                        {normalizePhotos(editForm.photos).map((img, idx) => (
                          <div key={idx} style={{ position: "relative" }}>
                            <img
                              src={img}
                              alt={`Photo ${idx + 1}`}
                              style={{
                                width: 96,
                                height: 72,
                                objectFit: "cover",
                                border: "1px solid #ddd",
                                borderRadius: 8,
                                background: "#fff"
                              }}
                              onError={(e) => {
                                e.target.style.display = "none";
                              }}
                            />
                            <button
                              type="button"
                              onClick={() => removePhotoAtIndex(idx)}
                              title="Remove photo"
                              style={{
                                position: "absolute",
                                top: -6,
                                right: -6,
                                width: 20,
                                height: 20,
                                borderRadius: "50%",
                                border: "none",
                                background: THEME,
                                color: "#fff",
                                fontSize: 12,
                                lineHeight: "20px",
                                cursor: "pointer",
                                padding: 0
                              }}
                            >
                              ×
                            </button>
                          </div>
                        ))}
                      </div>
                    ) : (
                      <span style={{ fontSize: 12, color: "#888" }}>No photos uploaded yet</span>
                    )}
                    {normalizePhotos(editForm.photos).length > 0 && (
                      <button
                        type="button"
                        className="btn btn-sm"
                        style={{ marginTop: 8, background: "#f1f1f1", fontSize: 12 }}
                        onClick={() => setEditForm({ ...editForm, photos: [] })}
                      >
                        Remove all photos
                      </button>
                    )}
                  </div>
                  <div style={{ fontSize: 12, fontWeight: 700, color: THEME, marginBottom: 6 }}>signature</div>
                  <div
                    style={{
                      width: "100%",
                      border: "2px solid #8b1634",
                      borderRadius: "18px",
                      background: "#e9cfd6",
                      padding: "0.5rem",
                      textAlign: "center",
                      position: "relative",
                      minHeight: "90px",
                      display: "flex",
                      flexDirection: "column",
                      justifyContent: "center",
                      alignItems: "center",
                      pointerEvents: "none"
                    }}
                  >
                    {editForm.signature ? (
                      <img
                        src={normalizeImageSource(editForm.signature, "image/png")}
                        alt="HSEQ Representative Signature"
                        style={{
                          maxWidth: "100%",
                          maxHeight: "70px",
                          objectFit: "contain",
                          border: "1px solid #ddd",
                          borderRadius: "8px",
                          padding: "4px",
                          backgroundColor: "white",
                          opacity: 1,
                          filter: "contrast(1.1) brightness(1)"
                        }}
                        onError={(e) => {
                          e.target.style.display = "none";
                        }}
                      />
                    ) : (
                      <div style={{ color: "#888", fontSize: "14px", padding: "16px" }}>Signature (Disabled in Edit)</div>
                    )}
                  </div>
                </div>
              </div>
              <div className="modal-footer">
                <button type="button" className="btn btn-secondary" onClick={() => setShowEditModal(false)}>
                  Cancel
                </button>
                <button type="button" className="btn" style={{ background: THEME, color: "#fff", fontWeight: 600 }} onClick={handleEditSave}>
                  Submit
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      <AdminTaylorImportModal
        open={showImportModal}
        onClose={() => setShowImportModal(false)}
        title="Upload SF-015-C Rail HSEQ Site Inspection (2) File"
        importPath="rail-hseq-site-inspection/import-form"
        isAdminUser={isAdminUser}
        onImported={({ insertedRow }) => {
          if (insertedRow && insertedRow._id) {
            const normalizedRow = {
              ...insertedRow,
              isolationPossessionYesNo: isolationPossessionYesNoForSelect(
                insertedRow.isolationPossessionYesNo ?? insertedRow.isolationPossession
              ),
            };
            setRecords((prev) => [normalizedRow, ...prev]);
            setPage(1);
          }
        }}
      />

      {showDeleteModal && (
        <div className="modal fade show" style={{ display: "block", background: "rgba(0,0,0,0.3)" }} tabIndex="-1">
          <div className="modal-dialog modal-dialog-centered">
            <div className="modal-content">
              <div className="modal-header">
                <h5 className="modal-title">Delete SF-015-C Rail HSEQ Site Inspection (2)</h5>
                <button type="button" className="btn-close" onClick={() => setShowDeleteModal(false)} />
              </div>
              <div className="modal-body">Are you sure you want to delete this record?</div>
              <div className="modal-footer">
                <button type="button" className="btn btn-secondary" onClick={() => setShowDeleteModal(false)}>
                  Cancel
                </button>
                <button type="button" className="btn btn-danger" onClick={confirmDelete}>
                  Delete
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </>
  );
}

export default RailHseqSiteInspectionPage;
